import { useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { If } from "lib/@components/switch"
import { navigate } from "lib/routes/navigate"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { toDataCy } from "lib/to-data-cy"
import Iconify from "minimals-template/components/Iconify"
import { useDialog } from "lib/@hooks/useDialog"
import { isEnabled } from "lib/@components/feature"
import { FEATURE_FLAG_PROMOTE_REGIME } from "routes/optimiser/feature-flag-promote-regime"
import {
    formatFullMoney,
    formatLargeNumber,
    formatNumber,
    getCurrencySymbol,
    isLargeNumber,
    round2,
} from "../format-utils"
import { ComparisonRow } from "./comparison-row"
import { DataQualityWarnings } from "./comparison-data-quality-warnings"
import { VariantBreakdownDialog } from "./variant-breakdown-dialog"

export function VariantComparisonColumn({ item: variant, baseline, scenarioId }) {
    const {
        onRecalculateVariant,
        onSetAsDefault,
        onPromoteVariant,
        onViewTab,
        lowestCostId,
        highestCostId,
        mostCoverageId,
    } = useBoundContext()
    const canPromote = Boolean(onPromoteVariant && isEnabled(FEATURE_FLAG_PROMOTE_REGIME))
    const [menuAnchor, setMenuAnchor] = useState(null)
    const isLowestCost = lowestCostId != null && variant?.id === lowestCostId
    const isHighestCost = highestCostId != null && variant?.id === highestCostId
    const isMostCoverage = mostCoverageId != null && variant?.id === mostCoverageId
    const openBreakdown = useDialog(<VariantBreakdownDialog />, { maxWidth: "md", fullWidth: true })
    const costResult = variant?.costResult
    const currencySymbol = getCurrencySymbol(costResult?.currency || variant?.rateCardCurrency)
    const annualCost = costResult?.grandTotal ?? variant?.annualCost ?? 0
    const isCalculating = variant?.status === "calculating"
    // A failed recalculation preserves the last successful result, so keep showing its
    // figures and the breakdown action here exactly as the main summary does, with the
    // failure surfaced as a warning. Only a result that is itself an error shape (no
    // previous result to fall back on) collapses the column.
    const hasUsableResult = !!costResult && !costResult.error
    const isError = variant?.status === "error" && !hasUsableResult
    const isStaleAfterFailure = variant?.status === "error" && hasUsableResult
    const isReady = hasUsableResult && (variant?.status === "calculated" || isStaleAfterFailure)

    const isBaseline = !baseline || baseline.id === variant?.id
    const baselineCost = isBaseline ? null : baseline?.costResult

    const taskCount = costResult?.taskCount ?? 0
    const costedTaskCount = costResult?.costedTaskCount ?? 0
    const facilityCount = costResult?.facilityBreakdown?.length ?? 0
    const tradeCount = costResult?.tradeBreakdown?.length ?? 0
    const totalAnnualHours = costResult?.totalAnnualHours ?? 0
    const rulesAppliedCount = variant?.ruleSetNames?.length ?? (variant?.ruleSetName ? 1 : 0)
    const totalAssets = useMemo(sumAssets, [costResult])
    const baselineTotalAssets = useMemo(sumBaselineAssets, [baselineCost])

    return (
        <Stack data-cy={toDataCy("variant-comparison", variant.id)} sx={{ flex: 1, minWidth: 300 }} spacing={2}>
            <Card sx={{ height: 210, display: "flex" }}>
                <CardContent sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                    <Stack spacing={1.5} sx={{ flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3, fontSize: 18 }}>
                                {variant.name}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <If value={isBaseline}>
                                    <Tooltip title="Current default variant for this scenario">
                                        <Chip label="Default" size="small" color="primary" sx={{ height: 22 }} />
                                    </Tooltip>
                                </If>
                                <If value={isLowestCost}>
                                    <Tooltip title="Lowest annual cost of the compared variants">
                                        <Chip
                                            data-cy="comparison-lowest-chip"
                                            label="Lowest"
                                            size="small"
                                            color="success"
                                            sx={{ height: 22, color: "common.white" }}
                                        />
                                    </Tooltip>
                                </If>
                                <If value={isHighestCost}>
                                    <Tooltip title="Highest annual cost of the compared variants">
                                        <Chip
                                            label="Highest"
                                            size="small"
                                            color="error"
                                            sx={{ height: 22, color: "common.white" }}
                                        />
                                    </Tooltip>
                                </If>
                                <If value={isMostCoverage}>
                                    <Tooltip title="Greatest number of costed tasks across the compared variants">
                                        <Chip
                                            label="Most Coverage"
                                            size="small"
                                            color="info"
                                            sx={{ height: 22, color: "common.white" }}
                                        />
                                    </Tooltip>
                                </If>
                                <IconButton aria-label="More options" size="small" onClick={openMenu} sx={{ ml: 0.5 }}>
                                    <Iconify icon="eva:more-vertical-fill" width={20} />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={closeMenu}
                                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                    <MenuItem onClick={handleSetDefault} disabled={isBaseline}>
                                        Set as Default
                                    </MenuItem>
                                    <If value={canPromote}>
                                        <MenuItem onClick={handlePromote} data-cy="variant-promote">
                                            Promote to Draft Regime
                                        </MenuItem>
                                    </If>
                                </Menu>
                            </Stack>
                        </Stack>

                        <Divider sx={{ borderStyle: "dashed" }} />

                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ flexWrap: "wrap", gap: 1.5, color: "text.primary" }}
                        >
                            <Stack direction="row" alignItems="center" spacing={0.75}>
                                <Iconify icon="lucide:building" sx={{ fontSize: 20, flexShrink: 0 }} />
                                <Typography variant="body2" sx={{ fontSize: 16 }}>
                                    {facilityCount} {facilityCount === 1 ? "Facility" : "Facilities"}
                                </Typography>
                            </Stack>
                            <Box
                                sx={{
                                    width: 3,
                                    height: 3,
                                    borderRadius: "50%",
                                    bgcolor: "text.disabled",
                                    flexShrink: 0,
                                }}
                            />
                            <Tooltip
                                title={totalAssets === 0 ? "0 assets currently synced to regime" : ""}
                                disableHoverListener={totalAssets !== 0}
                                disableFocusListener={totalAssets !== 0}
                                disableTouchListener={totalAssets !== 0}
                            >
                                <Stack direction="row" alignItems="center" spacing={0.75}>
                                    <Iconify icon="proicons:box" sx={{ fontSize: 20, flexShrink: 0 }} />
                                    <Typography variant="body2" sx={{ fontSize: 16 }}>
                                        {formatNumber(totalAssets)} {totalAssets === 1 ? "Asset" : "Assets"}
                                    </Typography>
                                </Stack>
                            </Tooltip>
                        </Stack>

                        <If value={isReady}>
                            <Divider sx={{ borderStyle: "dashed" }} />
                            <ComparisonRow
                                label="Annual Cost"
                                icon="majesticons:money-line"
                                value={formatLargeNumber(annualCost, currencySymbol)}
                                bold
                                delta={computeDelta(annualCost, baselineCost?.grandTotal)}
                                deltaPrefix={currencySymbol}
                                deltaFormat="money"
                                valueTooltip={getAnnualCostTooltip()}
                            />
                        </If>
                    </Stack>
                </CardContent>
            </Card>

            <If value={isCalculating}>
                <Card>
                    <CardContent>
                        <Stack alignItems="center" spacing={1} sx={{ py: 3 }}>
                            <CircularProgress size={24} />
                            <Typography variant="body2" color="text.secondary">
                                Calculating...
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            </If>

            <If value={isError}>
                <Card>
                    <CardContent>
                        <Stack spacing={1} alignItems="center">
                            <Typography variant="body2" color="error">
                                Calculation error
                            </Typography>
                            <If value={onRecalculateVariant}>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Iconify icon="mdi:refresh" />}
                                    onClick={handleRetry}
                                >
                                    Retry
                                </Button>
                            </If>
                        </Stack>
                    </CardContent>
                </Card>
            </If>

            <If value={isStaleAfterFailure}>
                <Alert severity="warning" sx={{ py: 0.25 }} data-cy="variant-comparison-stale-warning">
                    Last recalculation failed. Showing the previous result.
                </Alert>
            </If>

            <If value={isReady}>
                <Box data-cy="variant-comparison-overview">
                    <ComparisonRow
                        label="Tasks"
                        value={`${formatNumber(taskCount)} Tasks`}
                        subtitle={`${formatNumber(costedTaskCount)} costed`}
                        delta={computeDelta(taskCount, baselineCost?.taskCount)}
                    />
                    <ComparisonRow
                        label="Assets"
                        value={`${formatNumber(totalAssets)} Assets`}
                        subtitle={totalAssets === 0 ? null : `${formatNumber(totalAssets)} covered`}
                        delta={computeDelta(totalAssets, baselineTotalAssets)}
                        invertColor
                        valueTooltip={totalAssets === 0 ? "0 assets currently synced to regime" : null}
                    />
                    <ComparisonRow
                        label="Facilities"
                        value={formatNumber(facilityCount)}
                        delta={computeDelta(facilityCount, baselineCost?.facilityBreakdown?.length)}
                        onClick={facilityCount ? handleViewFacilities : null}
                    />
                    <ComparisonRow
                        label="Trades"
                        value={formatNumber(tradeCount)}
                        delta={computeDelta(tradeCount, baselineCost?.tradeBreakdown?.length)}
                        onClick={tradeCount ? handleViewTrades : null}
                    />
                    <ComparisonRow
                        label="Rate Card"
                        value={variant.rateCardName}
                        onClick={variant.rateCardId ? handleViewRateCard : null}
                    />
                    <ComparisonRow
                        label="Total Annual Hours"
                        value={`${round2(totalAnnualHours)}h`}
                        delta={computeDelta(totalAnnualHours, baselineCost?.totalAnnualHours)}
                    />
                    <ComparisonRow label="Rules Applied" value={formatNumber(rulesAppliedCount)} />
                </Box>
                <DataQualityWarnings costResult={costResult} />
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<Iconify icon="mdi:table-eye" />}
                    onClick={handleViewBreakdown}
                    fullWidth
                >
                    View Breakdown
                </Button>
            </If>
        </Stack>
    )

    function sumAssets() {
        return (costResult?.facilityBreakdown || []).reduce((sum, f) => sum + (f.assetCount || 0), 0)
    }

    function sumBaselineAssets() {
        if (!baselineCost) return null
        return (baselineCost?.facilityBreakdown || []).reduce((sum, f) => sum + (f.assetCount || 0), 0)
    }

    function computeDelta(current, baselineValue) {
        if (isBaseline || baselineValue == null) return null
        const diff = current - baselineValue
        return diff === 0 ? null : diff
    }

    function getAnnualCostTooltip() {
        if (!isLargeNumber(annualCost)) return null
        return formatFullMoney(annualCost, currencySymbol)
    }

    function handleViewBreakdown() {
        openBreakdown({ variant, scenarioId })
    }

    function handleRetry() {
        onRecalculateVariant?.(variant.id)
    }

    function openMenu(e) {
        setMenuAnchor(e.currentTarget)
    }

    function closeMenu() {
        setMenuAnchor(null)
    }

    function handleSetDefault() {
        onSetAsDefault?.(variant.id)
        closeMenu()
    }

    function handlePromote() {
        closeMenu()
        onPromoteVariant?.(variant)
    }

    function handleViewFacilities() {
        onViewTab?.("facilities")
    }

    function handleViewTrades() {
        onViewTab?.("trades")
    }

    function handleViewRateCard() {
        if (variant.rateCardId) navigate(`/app/optimiser/rate-cards/${variant.rateCardId}`)
    }
}
